import { useFirestore } from "reactfire";
import { collection, deleteDoc, doc, getDocs, query, setDoc, writeBatch } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { LineItemOrderDefinition, EstimateMatchCriteria, LineItemOrderObject } from "../pages/types";
import { v4 as uuidv4 } from "uuid";

export const useLineItemOrderDefinitions = () => {
	const firestore = useFirestore();
	const [lineItemOrderDefinitions, setLineItemOrderDefinitions] = useState<LineItemOrderDefinition[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);

	const fetchLineItemOrderObjects = useCallback(async () => {
		setIsLoading(true);

		const col = collection(firestore, "line_item_order_v2");
		const topLevelSnapshot = await getDocs(query(col));

		const definitions = await Promise.all(
			topLevelSnapshot.docs.map(async (docSnap) => {
				const data = docSnap.data();
				const subCollection = collection(firestore, "line_item_order_v2", docSnap.id, "line_item_order_objects");
				const subSnapshot = await getDocs(query(subCollection));
				const lineItemOrderObjects = subSnapshot.docs.map((subDoc) => subDoc.data() as LineItemOrderObject);

				return {
					uniqueId: docSnap.id,
					matchCriteria: data.matchCriteria,
					lineItemOrderObjects,
				} as LineItemOrderDefinition;
			}),
		);

		setLineItemOrderDefinitions(definitions);
		setIsLoading(false);
	}, [firestore]);

	useEffect(() => {
		fetchLineItemOrderObjects();
	}, [fetchLineItemOrderObjects]);

	const saveLineItemOrderObjects = useCallback(
		async (definitionId: string | undefined, matchCriteria: EstimateMatchCriteria, lineItemOrderObjects: LineItemOrderObject[]) => {
			try {
				setIsUpdating(true);
				const isNewDefinition = !definitionId;
				const uniqueId = definitionId ?? uuidv4();

				// if document doesn't already exist, create new doc with selected match criteria
				if (!definitionId) {
					// filter out key/value pairs where the value is undefined so we can save to firestore
					const matchCriteriaWithDefinedFields = Object.fromEntries(Object.entries(matchCriteria).filter(([_, value]) => value !== undefined))

					const parentDocRef = doc(firestore, "line_item_order_v2", uniqueId);
					await setDoc(parentDocRef, { matchCriteria: matchCriteriaWithDefinedFields });
				}

				const batch = writeBatch(firestore);
				lineItemOrderObjects.forEach((item) => {
					const docRef = doc(firestore, "line_item_order_v2", uniqueId, "line_item_order_objects", item.lineItemId);
					batch.set(docRef, item, { merge: true });
				});
				await batch.commit();

				const updatedLineItemOrderDefinition: LineItemOrderDefinition = {
					matchCriteria,
					lineItemOrderObjects,
					uniqueId,
				};

				// optimistically update state with updates passed into this function
				if (isNewDefinition) {
					setLineItemOrderDefinitions((_lineItemOrderDefinitions) => [..._lineItemOrderDefinitions, updatedLineItemOrderDefinition]);
				} else {
					setLineItemOrderDefinitions((_lineItemOrderDefinitions) =>
						_lineItemOrderDefinitions.map((_lineItemOrderDefinition) =>
							_lineItemOrderDefinition.uniqueId === uniqueId ? updatedLineItemOrderDefinition : _lineItemOrderDefinition,
						),
					);
				}
			} finally {
				setIsUpdating(false);
			}
		},
		[firestore, fetchLineItemOrderObjects, setLineItemOrderDefinitions],
	);

	const clearLineItemOrderObjects = useCallback(
		async (definitionId: string) => {
			try {
				setIsUpdating(true);
				const subCollection = collection(firestore, "line_item_order_v2", definitionId, "line_item_order_objects");
				const snapshot = await getDocs(query(subCollection));
				const batch = writeBatch(firestore);
				snapshot.docs.forEach((docSnap) => batch.delete(docSnap.ref));
				await batch.commit();

				setLineItemOrderDefinitions((_defs) =>
					_defs.map((_def) => (_def.uniqueId === definitionId ? { ..._def, lineItemOrderObjects: [] } : _def)),
				);
			} finally {
				setIsUpdating(false);
			}
		},
		[firestore],
	);

	return { lineItemOrderDefinitions, saveLineItemOrderObjects, clearLineItemOrderObjects, isLoading, isUpdating };
};
